import {
  LiveCampaignData,
  SelectedInfluencer,
} from "../CampaignInterface/LiveCampaignInterface";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/shadcnComponents/ui/accordion";
import {
  Card,
  CardContent,
  CardTitle,
  CardHeader,
} from "@/shadcnComponents/ui/card";
import { Target, Briefcase, Users, Layers, Link } from "lucide-react";
import { CampaignRequirement } from "../CampaignCreationForms/ReviewForm";
import { OpenInNewWindow } from "@/Helpers/helper";

interface LiveCollabInformationProps {
  collabData: LiveCampaignData;
  masterCollabGoals: {
    CollabGoalsName: string;
    _id: string;
  }[];
  masterContentCategory: {
    categoryName: string;
    _id: string;
  }[];
  masterAudienceCategory: {
    LkdAudienceTypeName: string;
    _id: string;
  }[];
  selectedInfluencers: SelectedInfluencer[];
}

export const LiveCollabInformation = ({
  collabData,
  masterCollabGoals,
  masterContentCategory,
  selectedInfluencers,
  masterAudienceCategory,
}: LiveCollabInformationProps) => {
  return (
    <div className="flex w-full flex-col items-start  py-3 md:py-6 lg:py-12 px-[24px]">
      <h1 className="text-[#121212] text-2xl not-italic font-bold leading-6">
        Collaboration Information
      </h1>

      <Accordion
        type="multiple"
        defaultValue={["campaign-info"]}
        className="space-y-4 w-full mt-4"
      >
        {/* Campaign Info Card */}
        <AccordionItem value="campaign-info">
          <Card>
            <AccordionTrigger className="bg-gradient-to-r py-0 from-purple-100 to-pink-100">
              <CardHeader className="p-4 hover:no-underline w-full">
                <div className="flex items-center justify-between w-full">
                  <CardTitle className="text-lg text-purple-800 flex items-center gap-2">
                    <Briefcase className="h-5 w-5" />
                    Campaign Info
                  </CardTitle>
                </div>
              </CardHeader>
            </AccordionTrigger>
            <AccordionContent>
              <CardContent className="p-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Goal
                    </span>
                    <span className="text-sm text-gray-800 bg-purple-100 px-2 py-1 rounded-full">
                      {collabData?.shortlistingCriteria?.collabCriteria?.collabPurpose
                        ?.map(
                          (item) =>
                            masterCollabGoals?.find(
                              (goal) => goal?._id === item
                            )?.CollabGoalsName
                        )
                        ?.join(",")}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Metric
                    </span>
                    <span className="text-sm text-gray-800 bg-blue-100 px-2 py-1 rounded-full">
                      {collabData?.shortlistingCriteria?.collabCriteria?.collabGoals?.replace(
                        "member reach",
                        "Member Reach"
                      )}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Platform
                    </span>
                    <span className="text-sm text-gray-800 bg-green-100 px-2 py-1 rounded-full">
                      {collabData?.shortlistingCriteria?.collabCriteria?.collabPlatform?.replace(
                        "linkedin",
                        "LinkedIn"
                      )}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Industry Area
                    </span>
                    <span className="text-sm text-gray-800 bg-yellow-100 px-2 py-1 rounded-full">
                      {
                        collabData?.shortlistingCriteria?.collabCriteria
                          ?.collabIndustry
                      }
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Business Type
                    </span>
                    <span className="text-sm text-gray-800 bg-red-100 px-2 py-1 rounded-full">
                      {collabData?.shortlistingCriteria?.collabCriteria?.collabBuisnessType?.toUpperCase()}
                    </span>
                  </div>
                </div>
              </CardContent>
            </AccordionContent>
          </Card>
        </AccordionItem>

        {/* Audience Criteria Card */}
        <AccordionItem value="audience-criteria">
          <Card>
            <AccordionTrigger className="bg-gradient-to-r py-0 from-green-100 to-teal-100">
              <CardHeader className=" p-4 hover:no-underline">
                <div className="flex items-center justify-between w-full">
                  <CardTitle className="text-lg text-green-800 flex items-center gap-2">
                    <Target className="h-5 w-5" />
                    Audience Criteria
                  </CardTitle>
                </div>
              </CardHeader>
            </AccordionTrigger>
            <AccordionContent>
              <CardContent className="p-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Job Roles
                    </span>
                    <span className="text-sm text-gray-800 bg-green-100 px-2 py-1 rounded-full">
                      {collabData?.shortlistingCriteria?.influencerCriteria
                        ?.audienceCategory?.length > 0
                        ? collabData?.shortlistingCriteria?.influencerCriteria?.audienceCategory
                            ?.map(
                              (item) =>
                                masterAudienceCategory?.find(
                                  (category) => category?._id === item
                                )?.LkdAudienceTypeName
                            )
                            ?.join(", ")
                        : "Not selected"}
                    </span>
                  </div>
                </div>
              </CardContent>
            </AccordionContent>
          </Card>
        </AccordionItem>

        {/* Influencer Criteria Card */}
        <AccordionItem value="influencer-criteria">
          <Card>
            <AccordionTrigger className="bg-gradient-to-r py-0 from-blue-100 to-indigo-100">
              <CardHeader className=" p-4 hover:no-underline">
                <div className="flex items-center justify-between w-full">
                  <CardTitle className="text-lg text-blue-800 flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Influencer Criteria
                  </CardTitle>
                </div>
              </CardHeader>
            </AccordionTrigger>
            <AccordionContent>
              <CardContent className="p-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Followers
                    </span>
                    <span className="text-sm text-gray-800 bg-blue-100 px-2 py-1 rounded-full">
                      {`${collabData?.shortlistingCriteria?.influencerCriteria?.followers}`}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Gender
                    </span>
                    <span className="text-sm text-gray-800 bg-pink-100 px-2 py-1 rounded-full">
                      {`${
                        collabData?.shortlistingCriteria?.influencerCriteria
                          ?.gender?.length
                          ? `${collabData?.shortlistingCriteria?.influencerCriteria?.gender}`
                          : "Not Selected"
                      }`}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Past Collaboration
                    </span>
                    <span className="text-sm text-gray-800 bg-yellow-100 px-2 py-1 rounded-full">
                      {`${
                        collabData?.shortlistingCriteria?.influencerCriteria
                          ?.isPastCollabs?.length
                          ? `${collabData?.shortlistingCriteria?.influencerCriteria?.isPastCollabs}`
                          : "Not Selected"
                      }`}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Post Format
                    </span>
                    <span className="text-sm text-gray-800 bg-purple-100 px-2 py-1 rounded-full">
                      {`${
                        collabData?.postInfo?.postDrafts?.filter(
                          (item) => item?.status === 1
                        )?.[0]?.postFormat?.length
                          ? `${collabData?.postInfo?.postDrafts
                              ?.filter((item) => item?.status === 1)?.[0]
                              ?.postFormat?.replace("/", "+")
                              ?.replace("text", "Text")}`
                          : "Not Selected"
                      }`}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-600">
                      Content Category
                    </span>
                    <span className="text-sm text-gray-800 bg-green-100 px-2 py-1 rounded-full">
                      {collabData?.shortlistingCriteria?.influencerCriteria
                        ?.contentCategory?.length > 0
                        ? collabData?.shortlistingCriteria?.influencerCriteria?.contentCategory
                            ?.map(
                              (item) =>
                                masterContentCategory?.find(
                                  (category) => category?._id === item
                                )?.categoryName
                            )
                            ?.join(", ")
                        : "Not selected"}
                    </span>
                  </div>
                </div>
              </CardContent>
            </AccordionContent>
          </Card>
        </AccordionItem>

        {/* Product Details Card */}
        <AccordionItem value="product-details">
          <Card>
            <AccordionTrigger className="bg-gradient-to-r py-0 from-yellow-100 to-orange-100">
              <CardHeader className=" p-4 hover:no-underline">
                <div className="flex items-center justify-between w-full">
                  <CardTitle className="text-lg text-orange-800 flex items-center gap-2">
                    <Layers className="h-5 w-5" />
                    Product Details
                  </CardTitle>
                </div>
              </CardHeader>
            </AccordionTrigger>
            <AccordionContent>
              <CardContent className="p-4">
                <div className="space-y-4">
                  <div>
                    <span className="text-sm font-medium text-gray-600">
                      Product Name
                    </span>
                    <p className="text-sm text-gray-800 mt-1">
                      {collabData?.productInfo?.productName}
                    </p>
                  </div>

                  {collabData?.productInfo?.productLink && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-600">
                        Reference Link
                      </span>
                      <div
                        onClick={() =>
                          OpenInNewWindow({
                            link: collabData?.productInfo?.productLink,
                          })
                        }
                        className="text-sm text-blue-600 underline flex items-center gap-1 cursor-pointer"
                      >
                        <Link className="h-4 w-4" />
                        {collabData?.productInfo?.productLink}
                      </div>
                    </div>
                  )}
                  <div>
                    <span className="text-sm font-medium text-gray-600">
                      About the Product/Service
                    </span>
                    <p className="text-sm text-gray-800 mt-1">
                      <div
                        className="ql-editor !p-0"
                        dangerouslySetInnerHTML={{
                          __html:
                            collabData?.productInfo?.productDescription ||
                            "N/A",
                        }}
                      />
                    </p>
                  </div>
                </div>
              </CardContent>
            </AccordionContent>
          </Card>
        </AccordionItem>
      </Accordion>

      <div className="w-full mt-4">
        <CampaignRequirement
          postInfo={collabData?.postInfo}
          shortlistedInfluencers={selectedInfluencers?.map(
            (item) => item?.influencerDetails
          )}
        />
      </div>
    </div>
  );
};
